import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { COMPANY_OWNERSHIP_API_SERVICE_TOKEN, DATA_SOURCE_API_SERVICE_TOKEN } from '@nk/api-lib';
import {
  CompanyTitleComponent,
  convertToSimpleOwnership,
  DEFAULT_SHARE_TYPE,
  OwnershipData,
  OwnershipViews,
} from '@nk/company-block';
import {
  CompanyShareOwnershipUltimateCompositeEntity,
  SimpleCollection,
  UnifiedOwnershipCompositeEntity,
} from '@nk/data-entities-lib';
import { PersonTitleComponent } from '@nk/person-block';
import { PercentagePipe } from '@nk/ui-lib';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { finalize, map, Observable, of, switchMap } from 'rxjs';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';

@Component({
  selector: 'nk-screening-report-company-ownership',
  imports: [AsyncPipe, CommonModule, TranslateModule, PersonTitleComponent, CompanyTitleComponent, PercentagePipe],
  templateUrl: './screening-report-company-ownership.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningReportCompanyOwnershipComponent implements OnInit {
  public companyUuid = input.required<string>();
  public screeningReportOwnershipData$!: Observable<OwnershipData | null>;
  public ownershipComposites$!: Observable<UnifiedOwnershipCompositeEntity[]>;
  public ownershipViews = OwnershipViews;

  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _dataSourceService = inject(DATA_SOURCE_API_SERVICE_TOKEN);
  private _companyOwnershipService = inject(COMPANY_OWNERSHIP_API_SERVICE_TOKEN);
  private _screeningReportOwnershipDataLoadingId = computed(
    () => `${this.constructor.name}_screeningReportOwnershipData`,
  );
  private _ownershipByCompanyLoadingId = computed(() => `${this.constructor.name}_screeningReportOwnershipData`);

  public ngOnInit(): void {
    this.screeningReportOwnershipData$ = this._screeningInputParamsService.screeningEntitiesInputParams$.pipe(
      switchMap((params) =>
        this._fetchOwnershipByCompany(params?.companyUuid).pipe(
          map((payload) => {
            if (!payload) {
              return null;
            }
            const result: OwnershipData = {
              data: payload.elements,
              totalCount: payload.count,
              shareIssuerCompanyUuidInput: params?.companyUuid,
              shareType: DEFAULT_SHARE_TYPE,
              viewType: OwnershipViews.Ultimate,
            };
            return result;
          }),
          finalize(() => {
            if (this._reportLoadingStateService) {
              this._reportLoadingStateService.setLoadingState(this._screeningReportOwnershipDataLoadingId(), false);
            }
          }),
        ),
      ),
    );

    this.ownershipComposites$ = this.screeningReportOwnershipData$.pipe(
      map((ownershipData) => {
        if (!ownershipData) {
          return [];
        }
        return ownershipData.data.map((el) =>
          convertToSimpleOwnership(
            ownershipData.shareIssuerCompanyUuidInput,
            ownershipData.shareholderCompanyUuidInput,
            el,
          ),
        );
      }),
    );
  }

  private _fetchOwnershipByCompany(
    companyUuid: string | undefined,
  ): Observable<SimpleCollection<CompanyShareOwnershipUltimateCompositeEntity> | null> {
    if (!companyUuid) {
      return of(null);
    }
    // ASSUMES NORWEGIAN OWNERSHIP!
    const dataSourceKey = 'ownership_norway';
    return this._dataSourceService.dataSourceAvailableYearCache.get(dataSourceKey).pipe(
      switchMap((years) => {
        return this._companyOwnershipService.companyShareOwnershipUltimateCompositeCollection.get({
          shareIssuerCompanyIdentifier: companyUuid,
          offset: 0,
          year: years[years.length - 1],
        });
      }),
      map((payload) => (payload ? payload : null)),
      finalize(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this._ownershipByCompanyLoadingId(), false);
        }
      }),
    );
  }
}
